import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../../context/LanguageContext";

/**
 * Marknadsplatsens andra sida.
 *
 * Startsidan säljer till kunden hela vägen ned, och en enda sektion vänder sig
 * till talangen. Den proportionen är rätt: en talang som söker sig hit kommer
 * via ``/for-talents`` eller via en kollega, medan kunden landar kallt.
 *
 * De fyra rutorna är valda för att de är de fyra frågor en frilansande artist
 * ställer innan hen registrerar sig, i den ordning hen ställer dem. Alla fyra
 * talen är verkliga: fjorton kalenderdagar står i Stripe-konfigurationen, tio
 * procent är avgiften, noll kronor i månadsavgift är beslutat, och både faktura
 * och DAC7-rapportering är byggda.
 *
 * Avgiften formuleras som "på bokningen" och inte "av ditt gage". Vår modell
 * lägger den ovanpå och kunden betalar den, så gaget är det talangen får. Det är
 * den enda siffran varje talang räknar på, och den får inte kunna missförstås.
 */

const T = {
  sv: {
    eyebrow: "För talanger",
    heading: "Bygg en verksamhet,\ninte bara ett gig.",
    body: "Vi sköter avtal, fakturor, betalningar och rapporteringen till Skatteverket. Du får förfrågningar som redan har datum, plats och budget, och svarar med en offert. Tio procent på bokningen, ingen månadsavgift, ingen bindningstid.",
    cta: "Skapa talangprofil",
    fine: "Klar på tio minuter. F-skatt krävs för att fakturera.",
    stats: [
      { v: "14 dgr", l: "Från genomfört event till utbetalning" },
      { v: "0 kr", l: "Månadsavgift och bindningstid" },
      { v: "Auto", l: "Faktura med rätt moms" },
      { v: "Auto", l: "DAC7 till Skatteverket" },
    ],
  },
  en: {
    eyebrow: "For talents",
    heading: "Build a business,\nnot just a gig.",
    body: "We handle agreements, invoices, payments and the reporting to the Swedish Tax Agency. You get requests that already have a date, a place and a budget, and you reply with an offer. Ten percent on the booking, no monthly fee, no lock-in.",
    cta: "Create a talent profile",
    fine: "Ready in ten minutes. Swedish F-skatt required to invoice.",
    stats: [
      { v: "14 days", l: "From event to payout" },
      { v: "0 kr", l: "Monthly fee and lock-in" },
      { v: "Auto", l: "Invoice with correct VAT" },
      { v: "Auto", l: "DAC7 to the Tax Agency" },
    ],
  },
};

const TalentCta = () => {
  const { lang } = useLang();
  const t = T[lang === "sv" ? "sv" : "en"];
  return (
    <section className="border-t border-[#1e1e1e]" style={{ background: "linear-gradient(135deg, #0F0F0F 0%, #1a0a04 100%)" }}
      data-testid="talent-cta">
      <div className="max-w-[1100px] mx-auto px-6 py-20 grid md:grid-cols-2 gap-10 lg:gap-14 items-center">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#FF7A2F] mb-4">{t.eyebrow}</div>
          <h2 className="text-[34px] md:text-[44px] font-extrabold tracking-tight text-white leading-[1.08] mb-4 whitespace-pre-line">
            {t.heading}
          </h2>
          <p className="text-[14.5px] text-neutral-400 leading-relaxed mb-7 max-w-[52ch]">{t.body}</p>
          <Link to="/signin?role=talent" data-testid="talent-cta-btn"
            className="inline-block bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-6 py-3.5 rounded-xl transition-colors">
            {t.cta}
          </Link>
          <div className="text-[12px] text-neutral-500 mt-3">{t.fine}</div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {t.stats.map((s) => (
            <div key={s.l} className="rounded-xl border border-[#2A2A2A] bg-[#181818] p-5">
              <div className="text-[24px] font-bold text-[#FF5C00]">{s.v}</div>
              <div className="text-[12.5px] text-neutral-400 mt-1.5 leading-snug">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TalentCta;
